import Model from "../Model.js";
import TranslatedString from "../TranslatedString.js";

/**
 * `abstractAccountGroup` is the root of the chart of accounts group
 * hierarchy. Groups can group other groups or intervals of accounts.
 *
 * Groups are implemented using a Composite Pattern.
 *
 * @abstract
 * @param {string} [spec.id] - Unique identifier of the group.
 * @param {string} [spec.name] - Name of the group
 * @param {boolean} spec.isKnownGroup - True if the group is a well known group in the chart of accounts.
 * @param {chartOfAccounts} [spec.chartOfAccounts] - Chart of accounts containing the new account group.
 */
export default class AbstractAccountGroup extends Model {
	constructor(spec = {}) {
		super(spec);

		this._name = spec.name;
		this._id = spec.id;
		this._isKnownGroup = spec.isKnownGroup;

		// This is needed to avoid a reference cycle:
		Object.defineProperty(this, "_chartOfAccounts", {
			enumerable: false,
			value: spec.chartOfAccounts,
			writable: true,
		});
	}

	isChildOf(parentAccountGroupId) {
		return this._chartOfAccounts.isChildOf({
			parentAccountGroupId,
			childAccountGroupId: this._id,
		});
	}

	getType() {
		return this.constructor.getType();
	}

	getChildren() {
		return [];
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getId() {
		return this._id;
	}

	displayLabel() {
		return this.getName();
	}

	getChartOfAccounts() {
		return this._chartOfAccounts;
	}

	setChartOfAccounts(chartOfAccounts) {
		if (this._chartOfAccounts) {
			throw new Error("This variable is only set during initialization.");
		}

		this._chartOfAccounts = chartOfAccounts;
	}

	getAllChildren() {
		const children = this.getChildren().slice();
		return children.reduce((acc, group) => {
			acc.push(...group.getAllChildren());
			return acc;
		}, children);
	}

	getAllGroups() {
		return this.getAllChildren().filter((child) => child.isAccountGroup());
	}

	withAllChildren() {
		return [this].concat(this.getAllChildren());
	}

	some(predicate) {
		if (predicate(this)) {
			return true;
		}

		return this.getChildren().some((child) => child.some(predicate));
	}

	count(predicate) {
		let result = 0;
		if (predicate(this)) {
			result++;
		}

		this.getChildren().forEach((child) => {
			result += child.count(predicate);
		});

		return result;
	}

	matchString(string) {
		return (
			this._name &&
			this._name.toString().toLowerCase().match(string.toLowerCase())
		);
	}

	hasChildren() {
		return this.getChildren().length !== 0;
	}

	hasAccounts() {
		this._subclassResponsibility();
	}

	hasAccount(_account) {
		this._subclassResponsibility();
	}

	representsGroup(group) {
		return this === group;
	}

	representsKnownGroup() {
		return (
			this.isKnownGroup() ||
			(this._chartOfAccounts &&
				this._chartOfAccounts.hasAliasingKnownGroup(this))
		);
	}

	asSpecification() {
		this._subclassResponsibility();
	}

	canBeEdited() {
		return true;
	}

	includesKnownGroup() {
		// there is no child
		return false;
	}

	isAliased() {
		return (
			this._chartOfAccounts &&
			this._chartOfAccounts.hasAliasingGroup(this)
		);
	}

	canRemove(_child) {
		// there is no child to remove by default
		return false;
	}

	canMoveDown(group) {
		const index = this.getChildren().indexOf(group);
		return index !== -1 && index !== this.getChildren().length - 1;
	}

	canMoveUp(group) {
		const index = this.getChildren().indexOf(group);
		return index !== -1 && index !== 0;
	}

	moveUp(parent) {
		return parent.moveUpGroup(this);
	}

	moveDown(parent) {
		return parent.moveDownGroup(this);
	}

	moveUpGroup(group) {
		const index = this.getChildren().indexOf(group);
		this.getChildren().splice(index, 1); // Remove group
		this.getChildren().splice(index - 1, 0, group); // Insert it at index-1
	}

	moveDownGroup(group) {
		const index = this.getChildren().indexOf(group);
		this.getChildren().splice(index, 1); // Remove group
		this.getChildren().splice(index + 1, 0, group); // Insert it at index+1
	}

	addChild(group) {
		if (!this.canAdd(group)) {
			throw new Error("Trying to add a non valid child");
		}

		this.getChildren().push(group);
	}

	isAccountGroup() {
		return false;
	}

	isKnownGroup() {
		return !!this._isKnownGroup;
	}

	isInterval() {
		return false;
	}

	isAccountList() {
		return false;
	}

	accept(visitor) {
		return visitor.visitAbstractAccountGroup(this);
	}

	includes(group) {
		return !!this.findById(group.getId());
	}

	findById(id) {
		if (id === this.getId()) {
			return this;
		}

		return null;
	}

	isChildIdValid(child) {
		return !this.some(
			(group) => group !== child && group.getId() === child.getId(),
		);
	}

	canAdd(group) {
		return group.canBeAddedTo(this);
	}

	canBeAddedTo(_group) {
		return this._subclassResponsibility();
	}

	canAddGroup(_group) {
		return true;
	}

	canAddInterval(_interval) {
		return true;
	}

	canAddAccountList(_interval) {
		return true;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.type = this.constructor.getType();

		properties.name = this._name;
		properties.id = this._id;
		properties.isKnownGroup = this._isKnownGroup;

		return properties;
	}

	static getType() {
		this.subclassResponsibility();
	}

	static transformMaterializationData(data) {
		data.name &&= new TranslatedString(data.name);
	}
}
